// src/pages/BulkUploadPage.jsx
import React, { useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import axios from "axios";
import "./BulkRegister.css"; // We'll add styling below

const BulkRegister = () => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [previewReady, setPreviewReady] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const token = localStorage.getItem("token");

  const formatEmployeeId = (employeeId) =>
    employeeId ? `CMPL${employeeId}` : "N/A";

  // 🔐 Generate random password
  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%";
    let password = "";
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // 🧩 Parse CSV file
  const parseCSV = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        handleParsedData(results.data);
      },
      error: (err) => alert("File parsing error: " + err.message),
    });
  };

  // 🧩 Parse Excel file
  const parseXLSX = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      handleParsedData(data);
    };
    reader.readAsBinaryString(file);
  };

  // 🧩 Handle file select
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "csv") parseCSV(file);
    else if (["xls", "xlsx"].includes(ext)) parseXLSX(file);
    else alert("Please upload a CSV or Excel file");
  };

  // 🧩 Validate rows
  const validateRows = (data) => {
    const newErrors = data.map((row) => {
      const err = [];
      if (!row.fullname) err.push("Missing Full Name");
      if (!row.email) err.push("Missing Email");
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim()))
        err.push("Invalid Email");
      if (!row.phone) err.push("Missing Phone");
      else if (!/^[6-9]\d{9}$/.test(row.phone.toString().trim()))
        err.push("Invalid Phone (10 digits starting with 6-9)");
      return err;
    });
    setErrors(newErrors);
  };

  const handleParsedData = (data) => {
  const normalized = data.map((item) => {
    const row = {};

    // Normalize all keys: lowercase + remove spaces
    for (let key in item) {
      const cleanKey = key.toLowerCase().replace(/\s+/g, "");
      row[cleanKey] = item[key];
    }

    // 🔧 Smart mapping (handles multiple possible header names)
    return {
      fullname: row.fullname || row.full_name || row.name || "",
      email: row.email || "",
      phone: row.phone || row.phonenumber || row.mobile || "",
      password: row.password || row.pass || row.pwd || generatePassword(),
    };
  });

  console.log("✅ Normalized Data:", normalized); // Debug

  setRows(normalized);
  validateRows(normalized);
  setPreviewReady(true);
  setReport(null);
};


  // 🧩 Send valid rows to backend
  const handleSubmit = async () => {
    const validRows = rows.filter((_, i) => errors[i].length === 0);
    if (validRows.length === 0) {
      alert("No valid rows to register!");
      return;
    }

    if (!window.confirm(`Proceed to register ${validRows.length} users?`))
      return;

    setUploading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/establishment/bulk-register`,
        {
          users: validRows,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setReport(data.results);
    } catch (error) {
      alert(
        "Upload failed: " +
          (error.response?.data?.message || error.response?.data?.error || error.message)
      );
    } finally {
      setUploading(false);
    }
  };

  // 🧩 Helper for UI
  const validCount = errors.filter((e) => e.length === 0).length;

  return (
    <div className="bulk-container">
      <h2>Bulk User Registration</h2>
      
      <div className="format-info">
        <h4>Expected CSV/Excel Format:</h4>
        <p>Your file should contain the following columns:</p>
        <ul>
          <li><strong>fullname</strong> - Full name of the user (required)</li>
          <li><strong>email</strong> - Email address (valid format required)</li>
          <li><strong>phone</strong> - Phone number (10 digits starting with 6-9)</li>
          <li><strong>password</strong> - Password (optional, will auto-generate if not provided)</li>
        </ul>
        <p><small>Note: Column names are case-insensitive and can contain spaces</small></p>
      </div>

      <div className="upload-box">
        <label className="upload-label">
          <input
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={handleFileChange}
          />
          <span>Select CSV / Excel File</span>
        </label>
      </div>

      {previewReady && (
        <>
          <div className="summary">
            <p>
              Total Rows: <b>{rows.length}</b>
            </p>
            <p>
              Valid Rows: <b>{validCount}</b>
            </p>
            <p>
              Invalid Rows: <b>{rows.length - validCount}</b>
            </p>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Password</th>
                  <th>Errors</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={i}
                    className={errors[i].length ? "invalid-row" : "valid-row"}
                  >
                    <td>{i + 1}</td>
                    <td>{row.fullname}</td>
                    <td>{row.email}</td>
                    <td>{row.phone}</td>
                    <td style={{fontFamily: 'monospace', backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '3px'}}>
                      {row.password}
                    </td>
                    <td>{errors[i].join(", ") || "✅"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="action-bar">
            <button
              onClick={handleSubmit}
              disabled={uploading}
              className="register-btn"
            >
              {uploading
                ? "Registering..."
                : `Register ${validCount} Valid Users`}
            </button>
          </div>
        </>
      )}

      {report && (
        <div className="report-section">
          <h3>Upload Report</h3>
          <table>
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {report.map((r, i) => (
                <tr key={i}>
                  <td>{r.success ? formatEmployeeId(r.employeeId) : "-"}</td>
                  <td>{r.fullname}</td>
                  <td>{r.email}</td>
                  <td>{r.success ? "✅ Success" : "❌ Failed"}</td>
                  <td>{r.errors?.join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BulkRegister;
